import React, { useEffect, useState } from "react";
import { Resident } from "@common";
import { useSessionContext, useSpinnerContext } from '@contexts';
import { sunboundPayProxy } from '@proxies';
import { PaymentMethodCard } from "./common/payment-method-card";
import { useNavigate } from "react-router-dom";

const ResolveDefaultPaymentMethods = () => {
    const {
        accounts,
        selectedAccount,
        session,
        setSession
    } = useSessionContext();
    const { setSpinnerShow } = useSpinnerContext();
    const navigate = useNavigate();
    const [handled, setHandled] = useState([]);

    // make sure we start with most up to date version of session
    // as we may have just updated the default payment method
    useEffect(() => {
        const fetchSession = async () => {
            const session = await sunboundPayProxy.refreshSession();
            setSession(session);
        }
        fetchSession();
    }, []);

    if (!accounts || accounts.length === 0) return null;

    // top account is always the resident we're navigated under
    const updatedAccount = accounts.find(a => a.idAccount === selectedAccount.idAccount);
    const otherAccounts = accounts.filter(a => a.idAccount !== selectedAccount.idAccount)
        .filter(a => !a.paymentPreferences.defaultPaymentMethod || a.paymentPreferences.defaultPaymentMethod.disabledAt)
        .filter(a => !handled.includes(a.idAccount));

    // account to resolve is just pulled off top of remaining unresolved session.accounts
    const nextAccount = otherAccounts[0];

    const handleNext = () => {
        if (otherAccounts.length > 1) {
            setHandled(prev => [...prev, nextAccount.idAccount]);
        } else {
            navigate('/');
        }
    };

    const handleMakeDefault = async () => {
        const idAccount = nextAccount.idAccount;
        const idPaymentMethod = updatedAccount.paymentPreferences.defaultPaymentMethod?.idPaymentMethod;
        if (!idAccount || !idPaymentMethod) {
            console.error('Invalid account or payment method');
            return;
        }

        setSpinnerShow(true);
        try {
            await sunboundPayProxy.upsertAccountDefaultPaymentMethod(idAccount, idPaymentMethod);
            const session = await sunboundPayProxy.refreshSession();
            setSession(session);
            handleNext();
        } catch (error) {
            console.error(error);
        }
        setSpinnerShow(false);
    };

    return (
        <div className="page-content-wrapper gap-y-6">
            <h1 className="page-header">Payment Method Updated</h1>

            <span className="page-subheader">You've updated the default payment method for:</span>
            <AccountDefaultPaymentMethod account={updatedAccount} />

            <span className="page-subheader">Would you like to make this payment method the default for:</span>
            { nextAccount && <AccountDefaultPaymentMethod account={nextAccount} /> }

            <div className="flex flex-col page-action gap-y-4">
                <button type="button" className="btn btn-primary" onClick={() => handleMakeDefault()}>Make Default</button>
                <button type="button" className="btn btn-outline-primary rounded-full font-bold text-blue-on text-xl" onClick={handleNext}>Skip</button>
            </div>

            { otherAccounts.length > 1 && <span className="page-subheader">{ otherAccounts.length - 1 } more to update</span> }
        </div>
    );
};

function AccountDefaultPaymentMethod({ account }) {
    const { idAccount, residentName, paymentPreferences } = account;
    return (
        <div key={idAccount} className="w-full rounded-xl border p-5 bg-white">
            <Resident residentName={residentName} />
            <div className="py-3">Payment Method:</div>
            { paymentPreferences.defaultPaymentMethod
                ? <PaymentMethodCard paymentMethod={paymentPreferences.defaultPaymentMethod} isDefault />
                : (
                    <div className="flex flex-col shadow-sb p-3 pl-5 rounded-lg bg-[#ECEBEB]">
                        <div className="font-bold text-lg">No Payment Method Selected</div>
                        <div className="text-gray-600">Please set a default payment method to this account</div>
                    </div>
                )
            }
        </div>
    );
}

export default ResolveDefaultPaymentMethods;